import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBase } from '../api';
import { useAuth } from '../components/AuthContext';

function money(cents) {
  if (!cents && cents !== 0) return '—';
  return `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

const statusConfig = {
  IN_TRANSIT:  { emoji: '🚛', color: '#2563eb', bg: '#dbeafe', label: 'In Transit' },
  PICKED_UP:   { emoji: '📦', color: '#d97706', bg: '#fef3c7', label: 'Picked Up' },
  IN_PROGRESS: { emoji: '🔄', color: '#7c3aed', bg: '#ede9fe', label: 'In Progress' },
  DELIVERED:   { emoji: '✅', color: '#059669', bg: '#d1fae5', label: 'Delivered' },
  COMPLETED:   { emoji: '✅', color: '#059669', bg: '#d1fae5', label: 'Completed' },
  CLOSED:      { emoji: '📁', color: '#6b7280', bg: '#f3f4f6', label: 'Closed' },
  TENDERED:    { emoji: '📋', color: '#0891b2', bg: '#cffafe', label: 'Tendered' },
  POSTED:      { emoji: '📢', color: '#dc2626', bg: '#fee2e2', label: 'Posted' },
  CREATED:     { emoji: '✏️', color: '#6b7280', bg: '#f3f4f6', label: 'Created' },
};

const FILTERS = ['ALL', 'IN_TRANSIT', 'PICKED_UP', 'DELIVERED', 'COMPLETED', 'TENDERED', 'POSTED', 'CREATED'];

export default function ShipperLoads() {
  const apiBase = useMemo(() => getApiBase(), []);
  const { shipperEmail } = useAuth();
  const nav = useNavigate();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!shipperEmail) return;
    setLoading(true);
    fetch(`${apiBase}/shipper/loads?status=${filter}`, {
      headers: { 'x-shipper-email': shipperEmail }
    })
      .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.json(); })
      .then(d => { setLoads(d.loads || []); setError(''); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [apiBase, shipperEmail, filter]);

  const filtered = search
    ? loads.filter(l =>
        (l.loadNumber || '').toLowerCase().includes(search.toLowerCase()) ||
        (l.originCity || '').toLowerCase().includes(search.toLowerCase()) ||
        (l.destCity || '').toLowerCase().includes(search.toLowerCase()) ||
        (l.carrierName || '').toLowerCase().includes(search.toLowerCase())
      )
    : loads;

  if (!shipperEmail) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
      Please set a Shipper Email in the dev panel.
    </div>
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>📦 My Shipments</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            placeholder="Search loads, cities, carriers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #d1d5db', width: 260, fontSize: 14 }}
          />
        </div>
      </div>

      {/* Status Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {FILTERS.map(f => {
          const sc = statusConfig[f] || { emoji: '📋', label: 'All' };
          const count = f === 'ALL' ? loads.length : loads.filter(l => l.status === f).length;
          const isActive = filter === f;
          return (
            <button key={f} onClick={() => setFilter(f)}
              style={{
                padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                border: isActive ? 'none' : '1px solid #e5e7eb', cursor: 'pointer',
                background: isActive ? (sc.color || '#374151') : 'white',
                color: isActive ? 'white' : '#374151',
                transition: 'all 0.15s'
              }}>
              {f === 'ALL' ? '📋 All' : `${sc.emoji} ${sc.label}`}
            </button>
          );
        })}
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', borderRadius: 8, color: '#991b1b', marginBottom: 16 }}>{error}</div>}

      {/* Loads Table */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            {search ? `No loads matching "${search}"` : 'No loads found'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['LOAD #', 'ORIGIN', 'DESTINATION', 'STATUS', 'CARRIER', 'RATE', 'DISTANCE', 'ETA'].map(h => (
                  <th key={h} style={{ textAlign: h === 'RATE' || h === 'DISTANCE' ? 'right' : 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(load => {
                const sc = statusConfig[load.status] || { emoji: '❓', color: '#6b7280', bg: '#f3f4f6', label: load.status };
                return (
                  <tr key={load.id} onClick={() => nav(`/app/shipper/loads/${load.id}`)}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '14px', fontWeight: 700, fontSize: 14 }}>{load.loadNumber}</td>
                    <td style={{ padding: '14px', fontSize: 14 }}>{load.originCity ? `${load.originCity}, ${load.originState}` : '—'}</td>
                    <td style={{ padding: '14px', fontSize: 14 }}>{load.destCity ? `${load.destCity}, ${load.destState}` : '—'}</td>
                    <td style={{ padding: '14px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: sc.bg, color: sc.color }}>
                        {sc.emoji} {sc.label}
                      </span>
                    </td>
                    <td style={{ padding: '14px', fontSize: 13, color: '#6b7280' }}>{load.carrierName || 'Pending'}</td>
                    <td style={{ padding: '14px', fontSize: 14, fontWeight: 600, textAlign: 'right' }}>{money(load.rateCents)}</td>
                    <td style={{ padding: '14px', fontSize: 13, color: '#6b7280', textAlign: 'right' }}>{load.distanceMiles ? `${load.distanceMiles} mi` : '—'}</td>
                    <td style={{ padding: '14px', fontSize: 13, color: '#6b7280' }}>
                      {load.eta ? new Date(load.eta).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
